import { buildLayoutStatsFromFeed } from './buildLayoutStatsFromFeed';
import { buildPartnerLayoutTree } from './layoutTreeUtils';
import { isShareEvent } from './shareEvents';

const emptyStats = () => ({
  opens: 0,
  sessions: 0,
  shuffles: 0,
  shares: 0,
  downloads: 0,
  lastActivityAt: null,
});

const emptyLifetime = () => ({
  lifetimeViews: 0,
  lifetimeShuffles: 0,
  lifetimeShares: 0,
  lifetimeDownloads: 0,
});

/** All nodes in a library tree branch (root + nested copies). */
export function collectSubtreePlans(node) {
  if (!node) return [];
  const out = [node];
  (node.children || []).forEach((child) => {
    out.push(...collectSubtreePlans(child));
  });
  return out;
}

function sessionKeyFromRow(row) {
  const u = row?.meta?.user;
  return (
    row?.meta?.guestSessionId ||
    (u && u !== 'guest' && u !== 'anonymous' ? u : null)
  );
}

function applyRowToRollup(bucket, row, excludeUsers, hasExclusions) {
  const ev = row?.meta?.event;
  const u = row?.meta?.user;
  const skipForExclusion = hasExclusions && u && excludeUsers.has(String(u));
  const stats = bucket.stats;

  if (!skipForExclusion) {
    if (ev === 'webview_opened') stats.opens += 1;
    if (ev === 'shuffle_room') stats.shuffles += 1;
    if (isShareEvent(ev)) stats.shares += 1;
    if (ev === 'download_layout') stats.downloads += 1;
  }

  const sk = sessionKeyFromRow(row);
  if (
    sk &&
    ev &&
    ev !== 'floorplan_uploaded' &&
    ev !== 'gallery_ready' &&
    !skipForExclusion
  ) {
    bucket.sessionKeys.add(String(sk));
  }

  if (row?.timestamp && !skipForExclusion) {
    const t = new Date(row.timestamp).getTime();
    if (Number.isFinite(t)) {
      const prev = stats.lastActivityAt ? new Date(stats.lastActivityAt).getTime() : 0;
      if (t > prev) stats.lastActivityAt = row.timestamp;
    }
  }
}

/**
 * One feed pass → period stats rolled up to each catalog root (sessions deduped
 * per lineage). O(feedRows + catalogNodes) instead of O(roots × feedRows).
 */
function buildLineagePeriodRollups(treeRoots, feedRows, excludeUsers = null) {
  const planToRootId = new Map();
  treeRoots.forEach((root) => {
    if (!root?._id) return;
    collectSubtreePlans(root).forEach((plan) => {
      if (plan._id) planToRootId.set(String(plan._id), root._id);
      if (plan.uuid) planToRootId.set(String(plan.uuid), root._id);
    });
  });

  const rollupByRootId = new Map();
  treeRoots.forEach((root) => {
    if (root._id) {
      rollupByRootId.set(root._id, { stats: emptyStats(), sessionKeys: new Set() });
    }
  });

  const hasExclusions = excludeUsers && excludeUsers.size > 0;
  (Array.isArray(feedRows) ? feedRows : []).forEach((row) => {
    const layoutId = row?.detail?.galleryId || row?.detail?.uniqueId;
    if (!layoutId) return;
    const rootId = planToRootId.get(String(layoutId));
    if (!rootId) return;
    const bucket = rollupByRootId.get(rootId);
    if (bucket) applyRowToRollup(bucket, row, excludeUsers, hasExclusions);
  });

  rollupByRootId.forEach((bucket) => {
    bucket.stats.sessions = bucket.sessionKeys.size;
  });

  return rollupByRootId;
}

/** Sum lifetime counters across parent + copies (no dedupe). */
export function aggregateLifetimeForPlans(plans) {
  return (plans || []).reduce(
    (acc, plan) => ({
      lifetimeViews: acc.lifetimeViews + (Number(plan?.lifetimeViews) || 0),
      lifetimeShuffles: acc.lifetimeShuffles + (Number(plan?.lifetimeShuffles) || 0),
      lifetimeShares: acc.lifetimeShares + (Number(plan?.lifetimeShares) || 0),
      lifetimeDownloads: acc.lifetimeDownloads + (Number(plan?.lifetimeDownloads) || 0),
    }),
    emptyLifetime()
  );
}

/**
 * Period stats for a set of layouts — used by unit tests; production rollup
 * uses {@link buildLineagePeriodRollups} for a single indexed feed pass.
 */
export function aggregatePeriodStatsForPlans(feedRows, plans, excludeUsers = null) {
  const planToRootId = new Map();
  (plans || []).forEach((plan) => {
    const token = plan?._id || '__lineage__';
    if (plan._id) planToRootId.set(String(plan._id), token);
    if (plan.uuid) planToRootId.set(String(plan.uuid), token);
  });

  const bucket = { stats: emptyStats(), sessionKeys: new Set() };
  const hasExclusions = excludeUsers && excludeUsers.size > 0;

  (Array.isArray(feedRows) ? feedRows : []).forEach((row) => {
    const layoutId = row?.detail?.galleryId || row?.detail?.uniqueId;
    if (!layoutId || !planToRootId.has(String(layoutId))) return;
    applyRowToRollup(bucket, row, excludeUsers, hasExclusions);
  });

  bucket.stats.sessions = bucket.sessionKeys.size;
  return bucket.stats;
}

/**
 * Per-layout direct stats plus lineage rollups for catalog roots.
 *
 * @param {Array} catalogLayouts flat or nested getByPartner rows
 * @param {Array} feedRows period-scoped analytics feed
 * @param {Set<string>|null} [excludeUsers] insider exclusion set
 */
export function buildLayoutStatsWithLineage(catalogLayouts, feedRows, excludeUsers = null) {
  const { lookup: lookupDirect } = buildLayoutStatsFromFeed(feedRows, excludeUsers);
  const treeRoots = buildPartnerLayoutTree(catalogLayouts || []);
  const periodRollups = buildLineagePeriodRollups(treeRoots, feedRows, excludeUsers);

  const lineageByRootId = new Map();

  treeRoots.forEach((root) => {
    if (!root?._id) return;
    const subtree = collectSubtreePlans(root);
    lineageByRootId.set(root._id, {
      periodStats: periodRollups.get(root._id)?.stats ?? emptyStats(),
      lifetimeStats: aggregateLifetimeForPlans(subtree),
      root,
    });
  });

  const lookupLineagePeriod = (plan) => {
    if (!plan?._id || !lineageByRootId.has(plan._id)) {
      return lookupDirect(plan);
    }
    return lineageByRootId.get(plan._id).periodStats;
  };

  const lookupLineageLifetime = (plan) => {
    if (!plan?._id || !lineageByRootId.has(plan._id)) return null;
    return lineageByRootId.get(plan._id).lifetimeStats;
  };

  const getHeroRootCandidates = () => {
    const ranked = [];
    treeRoots.forEach((root) => {
      if (root.isFork || !root._id) return;
      const entry = lineageByRootId.get(root._id);
      const shuffles = entry?.periodStats?.shuffles ?? 0;
      if (shuffles <= 0) return;
      ranked.push({
        _id: root._id,
        name: root.name || 'Floor plan',
        previewImage: root.blankImage || null,
        shuffleCount: shuffles,
      });
    });
    return ranked.sort((a, b) => b.shuffleCount - a.shuffleCount);
  };

  const countExploredRoots = () => {
    let count = 0;
    treeRoots.forEach((root) => {
      if (root.isFork || !root._id) return;
      const shuffles = lineageByRootId.get(root._id)?.periodStats?.shuffles ?? 0;
      if (shuffles > 0) count += 1;
    });
    return count;
  };

  return {
    lookupDirect,
    lookupLineagePeriod,
    lookupLineageLifetime,
    getHeroRootCandidates,
    countExploredRoots,
  };
}
